import queryString from 'query-string';

import { Competition } from '../../components/Competitions/CompetitionsTypes';
import {
  API_URL,
  ENDPOINT_VERSION,
  ENDPOINTS_ALPHAS,
  ENDPOINTS_COMPETITIONS,
  ENDPOINTS_DATA_EXPLORER,
  ENDPOINTS_OPERATORS,
  ENDPOINTS_USER,
  ENDPOINTS_VIDEO_COURSES,
  STREAK_START_DATE,
} from '../../config';

import { getResponseContent, GlobalFetchOptions } from '../helpers/fetch-helper';
import { getLanguage } from '../services/i18n';

import {
  datasetCoverageResponse,
  datasetTurnoverResponse,
  fieldCoverageResponse,
  fieldDistributionResponse,
  fieldRangeResponse,
  fieldTurnoverResponse,
  newestDatasetsResponse,
  popularDatasetsResponse,
  statsResponse,
} from './mocks/mocks';
import { FetchError } from './utils/fetchErrors';
import { ReferenceContent } from '../../reducers/LearnReducer/ReferencesReducer/ReferencesReducer.types';
import { HttpStatusCode } from './utils/statusCodes';

interface GetOptions extends GlobalFetchOptions {
  isCredentialsIncluded?: boolean;
  dispatch?: any;
  failureCallback?: () => void;
  headers?: Headers;
  isFullUrl?: boolean;
}

interface PostOptions extends GetOptions {
  body?: BodyInit | Record<any, any>;
  contentType?: ContentType;
}

type ContentType =
  | 'application/json'
  | 'application/x-www-form-urlencoded'
  | 'multipart/form-data'
  | 'text/html'
  | 'text/plain'
  | 'application/xml'
  | 'image/jpeg'
  | 'image/png'
  | 'AUTO';

const DEFAULT_SUCCESS_STATUSES = [200, 201, 202];
const GET_SUCCESS_STATUSES = [200];

export interface RequestOptions {
  headers?: Headers | undefined;
  body?: ReadableStream<any> | Blob | ArrayBufferView | ArrayBuffer | FormData | URLSearchParams | string | undefined | Record<any, any>;
  isCredentialsIncluded?: boolean | undefined;
  url: string;
  isResponseJson: boolean;
  method: 'GET' | 'PATCH' | 'POST' | 'OPTIONS';
  contentType?: ContentType;
  isFullUrl?: boolean;
  endpointVersion?: string;
  successStatuses?: number[];
  rawResponse?: boolean;
}

export function sendRequest(options: RequestOptions & { rawResponse: true }): Promise<Response>;
export function sendRequest(options: RequestOptions & { rawResponse?: false }): Promise<object>;
export function sendRequest({
  headers,
  body,
  isCredentialsIncluded,
  url,
  isResponseJson,
  method,
  contentType,
  isFullUrl = false,
  endpointVersion = ENDPOINT_VERSION,
  successStatuses = DEFAULT_SUCCESS_STATUSES,
  rawResponse = false,
}: RequestOptions): Promise<object | Response> {
  const requestHeaders = headers ? headers : new Headers();
  requestHeaders.append('Accept', `application/json;${endpointVersion}`);
  if (contentType && contentType !== 'AUTO') {
    requestHeaders.append('Content-Type', contentType);
  }
  // Inject the active language so the BE can localize responses without a DB lookup
  if (!requestHeaders.has('Accept-Language')) {
    requestHeaders.set('Accept-Language', getLanguage());
  }

  // Stringify the body if the content type is 'application/json'
  const requestBody = contentType === 'application/json' ? JSON.stringify(body) : body;

  const fetchOptions: RequestInit = {
    headers: requestHeaders,
    ...(requestBody && { body: requestBody as string }),
    method,
  };

  if (isCredentialsIncluded) {
    fetchOptions.credentials = 'include';
  }

  const fullUrl = isFullUrl ? url : `${API_URL}/${url}`;

  return fetch(fullUrl, fetchOptions).then(async (response): Promise<object | Response> => {
    if (!successStatuses.includes(response.status)) {
      let responseBody: unknown;
      try {
        responseBody = await response.json();
      } catch {
        // Ignore JSON parse errors
      }

      throw new FetchError(`response status: ${response.status}`, response, responseBody);
    }

    if (rawResponse) {
      return response;
    }

    return getResponseContent(response, isResponseJson);
  });
}

async function get(
  url: string,
  { headers, isResponseJson = true, isCredentialsIncluded = true, isFullUrl = false, endpointVersion = ENDPOINT_VERSION }: GetOptions = {}
) {
  return sendRequest({
    headers,
    isCredentialsIncluded,
    url,
    isResponseJson,
    method: 'GET',
    isFullUrl,
    endpointVersion,
    successStatuses: GET_SUCCESS_STATUSES,
  });
}

async function post(
  url: string,
  { body, headers, isResponseJson = true, isCredentialsIncluded = true, contentType, isFullUrl = false }: PostOptions = {}
) {
  return sendRequest({
    isFullUrl,
    headers: headers,
    body: body,
    isCredentialsIncluded: isCredentialsIncluded,
    url: url,
    isResponseJson: isResponseJson,
    method: 'POST',
    contentType,
  });
}

export async function requestOptions(
  url: string,
  { headers, isCredentialsIncluded = true, isResponseJson = true, contentType = 'application/json' }: Partial<PostOptions>
) {
  return sendRequest({
    headers: headers,
    isCredentialsIncluded: isCredentialsIncluded,
    url: url,
    isResponseJson: isResponseJson,
    method: 'OPTIONS',
    contentType,
  });
}

async function patch(
  url: string,
  { body, headers, isResponseJson = true, isCredentialsIncluded = true, contentType = 'application/json' }: PostOptions = {}
) {
  return sendRequest({
    headers: headers,
    body: body,
    isCredentialsIncluded: isCredentialsIncluded,
    url: url,
    isResponseJson: isResponseJson,
    method: 'PATCH',
    contentType,
  });
}

export type FetchAgreementParams =
  | { scope: 'public'; agreementId: string; enabled?: boolean }
  | { scope: 'competition'; competitionId: string; enabled?: boolean };

export async function fetchAgreement(params: FetchAgreementParams) {
  const endpoint =
    params.scope === 'public' ? `agreements/${params.agreementId}/agreement` : `competitions/${params.competitionId}/agreement`;
  return get(endpoint, { isCredentialsIncluded: false }) as Promise<{ title: string; id: string }>;
}

interface FetchReferralsParams {
  order: string;
  isAscending: boolean;
}

export interface ReferralResponse {
  count: number;
  next: string;
  previous: null;
  results: ReferralResult[];
}

export interface ReferralResult {
  inviteNumber: number;
  user: ReferralUser;
  bonusStatus?: string;
  bonus?: string;
}

export interface ReferralUser {
  id: string;
  country: string;
  status: string;
  bonusStatus?: string;
  signIn: string;
  startDate?: string;
  referrals?: string[];
}

export async function fetchReferrals({ order, isAscending }: FetchReferralsParams): Promise<ReferralResponse> {
  const orderWithDirection = isAscending ? order : `-${order}`;

  return get(`users/self/activities/referrals?${queryString.stringify({ order: orderWithDirection })}`) as Promise<ReferralResponse>;
}

export interface CompetitionSubmissionsResponse {
  count: number;
  next?: string;
  previous?: string;
  results: CompetitionSubmission[];
}

export interface CompetitionSubmission {
  id: string;
  dateSubmitted: string;
  fileName: string;
  description: string;
}

export async function fetchCompetitionSubmissions(competitionId: string): Promise<CompetitionSubmissionsResponse> {
  return get(`competitions/${competitionId}/submissions`) as Promise<CompetitionSubmissionsResponse>;
}

export async function deleteMachineAlphaSubmission(id: string): Promise<Record<string, any>> {
  return new Promise(resolve => setTimeout(() => resolve({}), 1000));
}

export function postCompetitionSubmission(competitionId: string, description: string, submissionFile: File) {
  let payload = new FormData();
  payload.append('submission', JSON.stringify({ description }));
  payload.append('file', submissionFile);

  return post(`competitions/${competitionId}/submissions`, { body: payload, isCredentialsIncluded: true });
}

export type PromptSubmissionStatus = 'Active' | 'Failed Validation';

export interface PromptSubmission {
  id: string;
  name: string;
  prompt: string;
  model: string;
  modelVersion: string;
  weight: number;
  updateFrequency: string;
  lastModified: string;
  status?: PromptSubmissionStatus;
}

export interface PromptSubmissionsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PromptSubmission[];
}

export interface UpdateFrequencyOption {
  value: string;
  label: string;
}

export interface PromptSubmissionOptions {
  model: UpdateFrequencyOption[];
  updateFrequency: UpdateFrequencyOption[];
}

export interface PromptWeightHistoryEntry {
  date: string;
  weight: number;
}

export interface PromptWeightHistoryResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PromptWeightHistoryEntry[];
}

export interface CreatePromptSubmissionPayload {
  name: string;
  prompt: string;
  model: string;
  modelVersion: string;
  weight: number;
  updateFrequency: string;
  sampleOutput?: string;
}

interface PromptSubmissionOptionsRaw {
  actions?: {
    POST?: {
      model?: { choices?: Array<{ value: string; label: string }> };
      updateFrequency?: { choices?: Array<{ value: string; label: string }> };
    };
  };
}

const PROMPT_SUBMISSIONS_ENDPOINT = 'competitions/spc/submissions';

export async function fetchPromptSubmissions(limit: number, offset: number): Promise<PromptSubmissionsResponse> {
  const response = (await get(`${PROMPT_SUBMISSIONS_ENDPOINT}?${queryString.stringify({ limit, offset })}`)) as PromptSubmissionsResponse;
  return {
    ...response,
    results: response.results.map(s => ({ ...s, weight: Number(s.weight) })),
  };
}

export async function postPromptSubmission(payload: CreatePromptSubmissionPayload): Promise<PromptSubmission> {
  return post(PROMPT_SUBMISSIONS_ENDPOINT, {
    body: payload,
    contentType: 'application/json',
    isCredentialsIncluded: true,
  }) as Promise<PromptSubmission>;
}

export async function patchPromptWeight(id: string, weight: number): Promise<PromptSubmission> {
  return patch(`${PROMPT_SUBMISSIONS_ENDPOINT}/${id}`, {
    body: { weight },
    contentType: 'application/json',
    isCredentialsIncluded: true,
  }) as Promise<PromptSubmission>;
}

export async function fetchPromptSubmissionOptions(): Promise<PromptSubmissionOptions> {
  const raw = (await requestOptions(PROMPT_SUBMISSIONS_ENDPOINT, {
    isCredentialsIncluded: true,
  })) as PromptSubmissionOptionsRaw;
  const post = raw?.actions?.POST ?? {};
  const toOptions = (field?: { choices?: Array<{ value: string; label: string }> }): UpdateFrequencyOption[] =>
    (field?.choices ?? []).map(c => ({ value: c.value, label: c.label }));
  return {
    model: toOptions(post.model),
    updateFrequency: toOptions(post.updateFrequency),
  };
}

export async function fetchPromptWeightHistory(id: string, limit: number, offset: number): Promise<PromptWeightHistoryResponse> {
  return get(`${PROMPT_SUBMISSIONS_ENDPOINT}/${id}?${queryString.stringify({ limit, offset })}`) as Promise<PromptWeightHistoryResponse>;
}

// ── SPC Leaderboard ──────────────────────────────────────────────────────────

const SPC_BOARD_ENDPOINT = 'consultant/boards/spc';

export interface SpcBoardChoice {
  value: string;
  label: string;
}

export interface SpcAggregateProperty {
  value: string;
  label: string;
}

export interface SpcAggregateFilter {
  value: string;
  label: string;
}

export interface SpcAggregateChoice {
  value: string;
  label: string;
  properties: SpcAggregateProperty[];
  filters: SpcAggregateFilter[];
}

export interface SpcFilterChoice {
  value: string;
  label: string;
}

export interface SpcLeaderboardOptions {
  aggregates: SpcAggregateChoice[];
  filterChoices: Record<string, SpcFilterChoice[]>;
}

export interface SpcLeaderboardRow {
  rank: number;
  [key: string]: string | number;
}

export interface SpcLeaderboardResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: SpcLeaderboardRow[];
}

interface SpcBoardOptionsRaw {
  actions?: {
    GET?: {
      board?: { choices?: Array<{ value: string; label: string }> };
    };
  };
}

interface SpcLeaderboardOptionsRaw {
  actions?: {
    GET?: {
      aggregate?: { choices?: SpcAggregateChoice[] };
      [filterKey: string]: { choices?: Array<{ value: string; label: string }> } | undefined;
    };
  };
}

export async function fetchSpcBoardChoices(): Promise<SpcBoardChoice[]> {
  const raw = (await requestOptions(SPC_BOARD_ENDPOINT, { isCredentialsIncluded: true })) as SpcBoardOptionsRaw;
  return raw?.actions?.GET?.board?.choices ?? [];
}

export async function fetchSpcLeaderboardOptions(board: string): Promise<SpcLeaderboardOptions> {
  const raw = (await requestOptions(`${SPC_BOARD_ENDPOINT}?board=${encodeURIComponent(board)}`, {
    isCredentialsIncluded: true,
  })) as SpcLeaderboardOptionsRaw;
  const getFields = raw?.actions?.GET ?? {};
  const aggregates = getFields.aggregate?.choices ?? [];
  const filterChoices: Record<string, SpcFilterChoice[]> = {};
  for (const [key, field] of Object.entries(getFields)) {
    if (key !== 'aggregate' && field?.choices) {
      filterChoices[key] = field.choices;
    }
  }
  return { aggregates, filterChoices };
}

export async function fetchSpcLeaderboard(params: {
  board: string;
  aggregate?: string;
  filters?: Record<string, string>;
  limit: number;
  offset: number;
}): Promise<SpcLeaderboardResponse> {
  const { board, aggregate, filters, limit, offset } = params;
  const qs = queryString.stringify({ board, aggregate, ...filters, limit, offset });
  return get(`${SPC_BOARD_ENDPOINT}?${qs}`) as Promise<SpcLeaderboardResponse>;
}

export interface FetchDatasetDescriptionParams {
  id: string;
}

export interface DatasetDescriptionData {
  region: Region;
  delay: number;
  universe: string;
  coverage: number;
  dateCoverage: number;
  userCount: number;
  alphaCount: number;
  fieldCount: number;
  themes: Theme[];
  new: boolean;
  id: string;
  dataset: {
    id: string;
    name: string;
  };
  'dataset.id': string;
  'dataset.name': string;
}

export interface DatasetDescriptionResponse {
  name: string;
  description: string;
  data: DatasetDescriptionData[];
  researchPapers: DatasetResource[];
  category: { id: string; name: string };
  subcategory: { id: string; name: string };
}

export async function fetchDatasetDescription({ id }: FetchDatasetDescriptionParams): Promise<DatasetDescriptionResponse> {
  return get(`${ENDPOINTS_DATA_EXPLORER.datasets}/${id}`, {
    isCredentialsIncluded: true,
    isFullUrl: true,
  }) as unknown as Promise<DatasetDescriptionResponse>;
}

export interface FetchFieldDescriptionParams {
  id: string;
}

export type FieldDescriptionResponse = {
  description: string;
  type: DataFieldType;
  data: FieldDescriptionData[];
  dataset: {
    id: string;
    name: string;
  };
  category: { id: string; name: string };
  subcategory: { id: string; name: string };
  visualizable?: boolean;
};

export type FieldDescriptionData = {
  region: Region;
  delay: number;
  universe: string;
  coverage: number;
  dateCoverage: number;
  userCount: number;
  alphaCount: number;
  themes?: Theme[];
  pyramidMultiplier?: number;
};

export async function fetchFieldDescription({ id }: FetchFieldDescriptionParams): Promise<FieldDescriptionResponse> {
  return get(`${ENDPOINTS_DATA_EXPLORER.dataFields}/${id}`, {
    isCredentialsIncluded: true,
    isFullUrl: true,
  }) as unknown as Promise<FieldDescriptionResponse>;
}
export interface PostFieldVisualizationParams {
  id: string;
  region: Region;
  delay: number;
  universe: string;
  instrumentType?: 'EQUITY';
}

export async function postFieldVisualization({ id, region, delay, universe, instrumentType = 'EQUITY' }: PostFieldVisualizationParams) {
  return post(`data-fields/${id}/visualize?instrumentType=${instrumentType}&region=${region}&delay=${delay}&universe=${universe}`, {
    isCredentialsIncluded: true,
    isResponseJson: false,
  }) as unknown as Promise<undefined>;
}

export interface GraphResponse {
  schema: GraphSchema;
  records: [string, ...number[]][];
}

export interface GraphSchema {
  name: string;
  title: string;
  properties: SchemaProperty[];
}

export interface SchemaProperty {
  name: string;
  title: string;
  type: string;
}

export interface FetchFieldCoverageParams {
  id: string;
  region: string;
  delay: number;
}

export async function fetchFieldCoverage({ id, region, delay }: FetchFieldCoverageParams): Promise<GraphResponse> {
  return Promise.resolve(fieldCoverageResponse) as Promise<GraphResponse>;
}

export interface FetchFieldTurnoverParams {
  id: string;
  region: string;
  delay: number;
}

export async function fetchFieldTurnover({ id, region }: FetchFieldTurnoverParams): Promise<GraphResponse> {
  return Promise.resolve(fieldTurnoverResponse) as Promise<GraphResponse>;
}

export interface FetchFieldRangeParams {
  id: string;
  region: string;
  delay: number;
}

export async function fetchFieldRange({ id, region }: FetchFieldRangeParams): Promise<GraphResponse> {
  return Promise.resolve(fieldRangeResponse) as Promise<GraphResponse>;
}

export interface FetchFieldDistributionParams {
  id: string;
  region: string;
}

export async function fetchFieldDistribution({ id, region }: FetchFieldDistributionParams): Promise<GraphResponse> {
  return Promise.resolve(fieldDistributionResponse) as Promise<GraphResponse>;
}

export async function fetchDatasets({ query }: FetchDatasetsParams) {
  return get(`${ENDPOINTS_DATA_EXPLORER.datasets}${query}`, {
    isCredentialsIncluded: true,
    isFullUrl: true,
  }) as unknown as Promise<DatasetsResponse>;
}

export interface FetchDatasetFieldsParams {
  query: string;
}

export interface FetchDatasetsParams {
  query: string;
}

export interface Theme {
  id: string;
  name: string;
  multiplier: number;
}

export interface PyramidMultiplier extends Theme {}

export interface Classification {
  id: string;
  name: string;
}

export interface DatasetField {
  id: string;
  description: string;
  delay: number;
  type: string;
  coverage: number;
  dateCoverage: number;
  themes?: Theme[];
  turnover: number;
  region: Region;
  userCount: number;
  new?: boolean;
  pyramidMultiplier?: number;

  alphaCount: number;
  valueScore: number;
  dateCreated?: string;
  dataset: {
    id: string;
    name: string;
    group: number;
  };
}

export type DataFieldType = 'MATRIX' | 'VECTOR' | 'GROUP' | 'UNIVERSE' | 'SYMBOL';

export type Region = 'USA' | 'CHN' | 'ASI' | 'EUR';

export interface DatasetsResponse {
  results: Dataset[];
  count: number;
  next?: boolean;
}

export interface DatasetResource {
  title: string;
  url: string;
  type: 'research' | 'discussion';
}

export interface Dataset {
  id: string;
  name: string;
  description: string;
  delay: number;
  coverage: number;
  dateCoverage: number;
  themes?: Theme[];
  turnover: number;
  region: Region;
  userCount: number;
  alphaCount: number;
  valueScore: number;
  fieldCount: number;
  researchPapers: DatasetResource[];
  new?: boolean;
  pyramidMultiplier?: number;
  dateUpdated?: string;
}

export interface DatasetFieldsResponse {
  results: DatasetField[];
  count: number;
  next?: boolean;
}

export async function fetchDatasetFields({ query }: FetchDatasetFieldsParams): Promise<DatasetFieldsResponse> {
  return get(`${ENDPOINTS_DATA_EXPLORER.dataFields}${query}`, {
    isCredentialsIncluded: true,
    isFullUrl: true,
  }) as unknown as Promise<DatasetFieldsResponse>;
}

export interface FetchAllDatasetFieldsParams {
  query: string;
}

export async function fetchAllDatasetFields({ query }: FetchAllDatasetFieldsParams): Promise<DatasetFieldsResponse> {
  return get(`${ENDPOINTS_DATA_EXPLORER.dataFields}${query}`, {
    isCredentialsIncluded: true,
    isFullUrl: true,
  }) as unknown as Promise<DatasetFieldsResponse>;
}

export interface DatasetFilterFieldsResponse {
  query: string;
}

export interface FilterRange {
  min?: string;
  max?: string;
}

export interface FetchDatasetCoverageParams {
  id: string;
  region: string;

  delay: number;
}

export async function fetchDatasetCoverage({ id, region, delay }: FetchDatasetCoverageParams): Promise<GraphResponse> {
  return Promise.resolve(datasetCoverageResponse) as Promise<GraphResponse>;
}

export interface FetchDatasetTurnoverParams {
  id: string;
  region: string;
  delay: number;
}

export async function fetchDatasetTurnover({ id, region, delay }: FetchDatasetTurnoverParams): Promise<GraphResponse> {
  return Promise.resolve(datasetTurnoverResponse) as Promise<GraphResponse>;
}

export type DataCategoriesResponse = DataCategory[];

export type DataCategory = {
  id: string;
  name: string;
  datasetCount: number;
  fieldCount: number;
  alphaCount: number;
  userCount: number;
  valueScore: number;
  region: string[];
  children?: DataCategory[];
};

export async function fetchDataCategories(): Promise<DataCategoriesResponse> {
  return get(ENDPOINTS_DATA_EXPLORER.dataCategories, {
    isCredentialsIncluded: true,
    isFullUrl: true,
  }) as unknown as Promise<DataCategoriesResponse>;
}

export interface DataStatsResponse {
  total: TotalStats;
  USA: RegionStats;
  EUR: RegionStats;
  ASI: RegionStats;
  CHN: RegionStats;
  new: RegionStats;
}

export interface TotalStats {
  datasets: number;
  fields: number;
  alphas: number;
  users: number;
}

export interface RegionStats {
  datasets: number;
  alphas: number;
}

export async function fetchDataStats(): Promise<DataStatsResponse> {
  return new Promise(resolve => setTimeout(() => resolve(statsResponse), 3000));
}

export interface TopDatasetsResponse {
  datasets: TopDataset[];
}

export interface TopDataset {
  id: string;
  name?: string;
  regions?: string[];
  delays?: number[];
  description?: string;
  themes?: Theme[];
}

export async function fetchMostPopularDatasets(): Promise<TopDatasetsResponse> {
  return new Promise(resolve => setTimeout(() => resolve(popularDatasetsResponse), 200));
}

export async function fetchNewestDatasets(): Promise<TopDatasetsResponse> {
  return new Promise(resolve => setTimeout(() => resolve(newestDatasetsResponse), 3000));
}

export async function fetchThemeDatasets(): Promise<TopDatasetsResponse> {
  return new Promise(resolve => setTimeout(() => resolve(newestDatasetsResponse), 3000));
}

export interface DataSearchParams {
  query: string;
}

export interface DataSearchResponse {
  fields: {
    id: string;
    description: string;
    dataset: {
      id: string;
      name: string;
    };
  }[];
  datasets: {
    id: string;
    name: string;
  }[];
}

export async function fetchDataSearch({ query }: DataSearchParams): Promise<DataSearchResponse> {
  return get(`${ENDPOINTS_DATA_EXPLORER.dataSearch}${query}`, {
    isCredentialsIncluded: true,
    isFullUrl: true,
  }) as unknown as Promise<DataSearchResponse>;
}

export interface HonorCategory {
  user: string;
  country: string;
  value: string;
}

export interface HonorCategoryByUser {
  experienced?: HonorCategory;
  rookie?: HonorCategory;
}

export interface CompetitionParams {
  id: string;
}

export type CompetitionResponse = Competition;

export async function fetchCompetition({ id }: CompetitionParams): Promise<CompetitionResponse> {
  return get(`${ENDPOINTS_COMPETITIONS.competitions}/${id}`, {
    isCredentialsIncluded: true,
    isFullUrl: true,
  }) as unknown as Promise<CompetitionResponse>;
}

export interface AlphaDistributionResponse {
  count: number;
  alphas: AlphaStatGroup[];
}

export type DataDiversityCheckValue = 'PASS' | 'WARNING' | 'FAIL';

export interface DataDiversityValue {
  check: DataDiversityCheckValue;
  limit: number;
}

export interface AlphaStatGroup {
  region?: string | null;
  delay?: number | null;
  dataCategory?: DataCategoryWithName | null;
  alphaCount: number;
  dataDiversity?: DataDiversityValue;
}

export interface DataCategoryWithName {
  id: string;
  name: string;
}

export async function fetchAlphaDistribution(hierarchy: string[]): Promise<AlphaDistributionResponse> {
  const grouping = hierarchy.join(',');

  return get(`${ENDPOINTS_USER.user}/self/activities/diversity?grouping=${grouping}`, {
    isCredentialsIncluded: true,
    isFullUrl: true,
  }) as Promise<AlphaDistributionResponse>;
}

interface FetchAlphaDiversityRangeParams {
  startDate?: string;
  endDate?: string;
}

export interface CategoryShort {
  id: string;
  name: string;
}

export interface AlphaDiversityRangeResponse {
  pyramids: Array<{ region: string; delay: number; category: CategoryShort; alphaCount: number }>;
}

export async function fetchAlphaDiversityRange({ startDate, endDate }: FetchAlphaDiversityRangeParams) {
  const searchParams = new URLSearchParams();
  if (startDate) searchParams.append('startDate', startDate);
  if (endDate) searchParams.append('endDate', endDate);

  return get(`${ENDPOINTS_USER.user}/self/activities/pyramid-alphas?${searchParams}`, {
    isCredentialsIncluded: true,
    isFullUrl: true,
  }) as Promise<AlphaDiversityRangeResponse>;
}

export interface AlphaPyramidMultipliersResponse {
  pyramids: Array<{ region: string; delay: number; category: CategoryShort; multiplier: number }>;
  regions: Array<{ name: string; delays: number[] }>;
  dataCategories: Array<{ id: string; name: string }>;
}

export async function fetchPyramidMultipliers() {
  return get(`${ENDPOINTS_USER.user}/self/activities/pyramid-multipliers`, {
    isCredentialsIncluded: true,
    isFullUrl: true,
  }) as Promise<AlphaPyramidMultipliersResponse>;
}

export interface FetchAlphaChartRecordSetParams {
  alphaId: string;
  chartType: string;
}

export interface FetchAlphaChartRecordSetResponse extends GraphResponse {}

export function fetchAlphaChartRecordSet({ alphaId, chartType }: FetchAlphaChartRecordSetParams) {
  return get(`${ENDPOINTS_ALPHAS.alpha}/${alphaId}/recordsets/${chartType}`, {
    isFullUrl: true,
    isCredentialsIncluded: true,
  }) as unknown as FetchAlphaChartRecordSetResponse;
}

export interface StreakDay {
  date: string;
  count: number;
  frozen: boolean;
}

export interface StreakFreezeInfo {
  eligible: boolean;
  quarter: string;
  usedDate: string | null;
  appliedYesterday: boolean;
}

export interface StreakResponse {
  active: number;
  activeToday: boolean;
  max: number;
  // BE returns exactly 7 entries
  records: StreakDay[];
  freeze: StreakFreezeInfo;
}

export interface SimulationsActivityResponse {
  yesterday: PeriodRecord;
  current: PeriodRecord;
  previous: PeriodRecord;
  ytd: PeriodRecord;
  total: PeriodRecord;
  records: SimulationRecords;
  type: string;
  frozenDates?: string[];
}

export interface SimulationActivityResult {
  records: [string, number][];
  frozenDates: string[];
}

export interface PeriodRecord {
  start: string;
  end: string;
  value: number;
}

export interface Total {
  start: string;
  end: string;
  value: number;
}

export interface SimulationRecords {
  schema: SimulationRecordsSchema;
  records: [string, number][];
}

export interface SimulationRecordsSchema {
  name: string;
  title: string;
  properties: SimulationRecordSchemaProperty[];
}

export interface SimulationRecordSchemaProperty {
  name: string;
  title: string;
  type: string;
}

export async function fetchStreak(): Promise<StreakResponse> {
  return get(`${ENDPOINTS_USER.user}/self/streak`, {
    isFullUrl: true,
    isCredentialsIncluded: true,
  }) as Promise<StreakResponse>;
}

// Derive freeze-protected days from the activity records. Per the BE decision on BRAINTECH-5854, the
// BE will NOT add a dedicated `frozenDates` field; instead a freeze-protected day is emitted as a
// `[date, 0]` record (a 0-count day means "saved by a streak freeze"). Ordinary no-activity days are
// simply absent from `records`, so a 0 count unambiguously signals a freeze. If BE ever does send an
// explicit `frozenDates`, we prefer it.
function deriveFrozenDates(response: SimulationsActivityResponse): string[] {
  return response.frozenDates ?? response.records.records.filter(([, count]) => count === 0).map(([date]) => date);
}

export async function fetchSimulationActivity(startDate: string = STREAK_START_DATE): Promise<SimulationActivityResult> {
  const response = (await get(`${ENDPOINTS_USER.user}/self/activities/simulations?date>=${startDate}`, {
    isFullUrl: true,
    isCredentialsIncluded: true,
  })) as SimulationsActivityResponse;
  return {
    records: response.records.records,
    frozenDates: deriveFrozenDates(response),
  };
}

export async function fetchSubmissionActivity(startDate: string = STREAK_START_DATE): Promise<[string, number][]> {
  const response = (await get(`${ENDPOINTS_USER.user}/self/activities/submissions?date>=${startDate}`, {
    isFullUrl: true,
    isCredentialsIncluded: true,
  })) as SimulationsActivityResponse;
  return response.records.records;
}

export interface FetchCreateProtoUserRequest {
  agreePrivacyPolicy: boolean;
  email: string;
  captcha: string;
}

export async function fetchCreateProtoUser(payload: FetchCreateProtoUserRequest): Promise<{}> {
  return post('proto-user', {
    body: payload as any,
    isCredentialsIncluded: true,
    contentType: 'application/json',
    isResponseJson: false,
  });
}

export interface CompetitionTeamResults {
  count: number;
  next: any;
  previous: any;
  results: string[];
}

interface FetchCompetitionTeamsByPrefixParams {
  competitionId: string;
  prefix: string;
  boardType?: string;
  boardRegion?: string;
}

// Map board tab-type to the consultant-boards path segment. Both spellings
// of power-pool are accepted: FilterMenu.tsx (formattedBoardType) normalizes
// 'powerPool' to 'power-pool' before passing it to the hook, but accepting
// the raw TabType too makes the map robust against future call sites.
const CONSULTANT_BOARD_URL_TYPE: Record<string, string> = {
  spcLeaderboard: 'spc',
  powerPool: 'power-pool',
  'power-pool': 'power-pool',
  referrals: 'referral',
};

export function consultantBoardUrlType(boardType?: string): string | undefined {
  return boardType ? CONSULTANT_BOARD_URL_TYPE[boardType] : undefined;
}

export async function fetchCompetitionTeamsByPrefix({
  competitionId,
  prefix,
  boardType,
  boardRegion,
}: FetchCompetitionTeamsByPrefixParams) {
  const requestBoardType = boardType ?? `leader`;
  const response = (await get(
    `${
      ENDPOINTS_COMPETITIONS.competitions
    }/${competitionId}/boards/${requestBoardType}?limit=10&offset=0&aggregate=team&order=team&property=team&team~${encodeURIComponent(
      prefix
    )}`,
    {
      isCredentialsIncluded: true,
      isFullUrl: true,
    }
  )) as CompetitionTeamResults;
  return response.results;
}

export async function fetchCompetitionUsersByPrefix({
  competitionId,
  prefix,
  boardType,
  boardRegion,
}: FetchCompetitionTeamsByPrefixParams) {
  const urlType = consultantBoardUrlType(boardType);
  const boardRegionsSuffix = boardRegion ? `&board=${encodeURIComponent(boardRegion)}` : '';

  // Consultant boards (SPC, power-pool, referral) are served from
  // /consultant/boards/<type> — the same path the leaderboard table fetch
  // uses — not /competitions/<id>/boards/<type>. competitionId is unused on
  // this branch (the path is board-scoped, not competition-scoped). Other
  // boards keep the /competitions/<id>/boards/<type> path.
  const base =
    urlType !== undefined
      ? `${ENDPOINTS_COMPETITIONS.consultant}/boards/${urlType}`
      : `${ENDPOINTS_COMPETITIONS.competitions}/${competitionId}/boards/${boardType ?? `leader`}`;

  const response = (await get(
    `${base}?limit=10&offset=0&aggregate=user&order=user&property=user&user~${encodeURIComponent(prefix)}${boardRegionsSuffix}`,
    {
      isCredentialsIncluded: true,
      isFullUrl: true,
    }
  )) as CompetitionTeamResults;
  return response.results;
}

export interface OsmosisData {
  region: string;
  delay: number;
  pointsAllocated: number;
  alphas: number;
  scalable?: boolean;
}

export interface ConsultantSummaryResponse {
  performance: Performance;
  osmosis?: OsmosisData[];
}

export interface ConsultantLeaderboard {
  user: string;
  weightFactor: number;
  valueFactor: number;
  dataFieldsUsed: number;
  submissionsCount: number;
  meanProdCorrelation: number;
  meanSelfCorrelation: number;
  superAlphaSubmissionsCount: number;
  superAlphaMeanProdCorrelation: number;
  superAlphaMeanSelfCorrelation: number;
  university: string;
  country: string;
}

export interface Performance {
  currentLevel: string;
  bestLevel: string;
  current: GeniusQuarterHistory;
  currentQuarter: Quarter;
  history: GeniusQuarterHistory[];
  previous: GeniusQuarterHistory;
}

export interface GeniusQuarterHistory extends GeniusStatsInfo {
  quarter: Quarter;
  level: string | null;
  geniusLevel: string | null;
  completedReferrals: number;
}

export interface GeniusStatsInfo {
  alphaCount: number;
  pyramidCount: number;
  combinedAlphaPerformance: number | null;
  combinedPowerPoolAlphaPerformance: number | null;
  combinedSelectedAlphaPerformance: number | null;
  combinedOsmosisPerformance: number | null;
  operatorCount: number;
  operatorAvg: number;
  fieldCount: number;
  fieldAvg: number;
  communityActivity: number | null;
  maxSimulationStreak: number;
}

export interface Quarter {
  endDate: string;
  name: string;
  startDate: string;
}

export interface FetchConsultantPerformanceParams {
  userId: string;
}

export async function fetchConsultantPerformance({ userId }: FetchConsultantPerformanceParams) {
  return (await get(`${process.env.REACT_APP_API_BASE}/users/${userId}/consultant/summary`, {
    isCredentialsIncluded: true,
    isFullUrl: true,
  })) as ConsultantSummaryResponse;
}

export interface OsmosisSummaryResponse {
  osmosis: OsmosisData[];
}

interface OsmosisUserParams {
  userId?: string;
}

export async function fetchOsmosisSummary({ userId = 'self' }: OsmosisUserParams = {}) {
  return (await get(`users/${userId}/osmosis/summary`)) as OsmosisSummaryResponse;
}

export async function triggerOsmosisScale({ userId = 'self' }: OsmosisUserParams = {}): Promise<void> {
  // Empty body => scale ALL scopes. 201 has no JSON body, so isResponseJson: false avoids a parse
  // error; a 303 (job already running) throws a FetchError the hook detects to resume polling.
  await post(`users/${userId}/osmosis/scale-points`, { isResponseJson: false });
}

export type OsmosisScaleStatus = 'in_progress' | 'done';

// BE-defined request-id for the "scale all scopes" job (trigger uses an empty body).
const OSMOSIS_SCALE_ALL_REQUEST_ID = 'ALL';

export interface OsmosisScaleStatusResult {
  status: OsmosisScaleStatus;
  retryAfterMs?: number;
}

// Parses a Retry-After header (delta-seconds or HTTP-date) to ms. Returns undefined for a
// missing/blank/unparseable/non-positive value so callers fall back to their default interval.
function parseRetryAfterMs(headerValue: string | null): number | undefined {
  const trimmed = headerValue?.trim();
  if (!trimmed) {
    return undefined;
  }

  // Number('') is 0 and Number('12abc') is NaN; require the whole string to be numeric.
  const seconds = Number(trimmed);
  if (!Number.isNaN(seconds)) {
    return seconds > 0 ? seconds * 1000 : undefined;
  }

  const dateMs = Date.parse(trimmed);
  if (!Number.isNaN(dateMs)) {
    const deltaMs = dateMs - Date.now();
    return deltaMs > 0 ? deltaMs : undefined;
  }

  return undefined;
}

export async function fetchOsmosisScaleStatus({ userId = 'self' }: OsmosisUserParams = {}): Promise<OsmosisScaleStatusResult> {
  // Always 200; in-progress is signalled by the presence of the Retry-After header (absent => done).
  const response = await sendRequest({
    url: `users/${userId}/osmosis/scale-points/${OSMOSIS_SCALE_ALL_REQUEST_ID}`,
    isCredentialsIncluded: true,
    isResponseJson: false,
    method: 'GET',
    successStatuses: [HttpStatusCode.Ok, HttpStatusCode.NoContent],
    rawResponse: true,
  });

  const retryAfterHeader = response.headers.get('Retry-After');
  if (retryAfterHeader !== null) {
    return { status: 'in_progress', retryAfterMs: parseRetryAfterMs(retryAfterHeader) };
  }

  return { status: 'done' };
}

export type OperatorLevel = 'ALL' | 'GENIUS';

export interface Operator {
  name: string;
  definition: string;
  description: string;
  category: string;
  scope: string[];
  documentation?: string;
  level?: OperatorLevel;
}

export async function fetchOperators(): Promise<Operator[]> {
  return (await get(`${ENDPOINTS_OPERATORS.operators}`, {
    isCredentialsIncluded: true,
    isFullUrl: true,
  })) as Operator[];
}

export interface LearnBlockResponse {
  id: string;
  title: string;
  content: ReferenceContent[];
  sequence: number;
  category: string;
}

export async function fetchOperatorDescription(operatorPath: string) {
  return (await get(`${process.env.REACT_APP_API_BASE}${operatorPath}`, {
    isCredentialsIncluded: true,
    isFullUrl: true,
  })) as Promise<LearnBlockResponse>;
}

export async function fetchAlphaTutorialState(): Promise<TutorialStateResponse> {
  return (await get(`${process.env.REACT_APP_API_BASE}/users/self/tutorial/summary`, {
    isCredentialsIncluded: true,
    isFullUrl: true,
  })) as Promise<TutorialStateResponse>;
}

export interface TutorialCheck {
  result: 'PASS' | 'FAIL';
  name: string;
}

export interface TutorialCheckResponse {
  checks: TutorialCheck[];
  step: string;
}

export async function fetchAlphaTutorialChecks(alphaId: string) {
  return (await get(`${process.env.REACT_APP_API_BASE}/alphas/${alphaId}/tutorial`, {
    isCredentialsIncluded: true,
    isFullUrl: true,
    endpointVersion: 'version=3.0',
  })) as Promise<TutorialCheckResponse>;
}

export async function fetchTutorialPageContent(pageSlug: string) {
  const tutorialEndpoint = '/tutorial/';
  return (await get(`${process.env.REACT_APP_API_BASE}${tutorialEndpoint}${pageSlug}`, {
    isCredentialsIncluded: true,
    isFullUrl: true,
  })) as Promise<LearnBlockResponse>;
}

export interface TutorialStateResponse {
  active: boolean;
  expired: boolean;
  startDatetime: string | null;
  expireDatetime: string | null;
  completionDate: string | null;
  currentStep: number | string;
  maxUnlockedStep: number;
  totalSteps: number;
  status: TutorialStatus;
  notificationSlug: string | null;
}

export async function patchTutorialStep(newStep: number) {
  const payload = { currentStep: newStep };
  return (await patch(`users/self/tutorial/summary`, {
    body: payload,
    isCredentialsIncluded: true,
  })) as Promise<ConsultantTutorialStepDetailsResponse>;
}

export async function patchTutorialActive(isActive: boolean) {
  const payload = { active: isActive };
  return (await patch(`users/self/tutorial/summary`, {
    body: payload,
    isCredentialsIncluded: true,
    isResponseJson: true,
    contentType: 'application/json',
  })) as Promise<ConsultantTutorialStepDetailsResponse>;
}

export async function patchTutorialExpired() {
  return (await post(`users/self/tutorial/summary`, {
    body: {},
    isCredentialsIncluded: true,
  })) as Promise<TutorialStateResponse>;
}

interface TutorialHint {
  name: string;
  task: string;
  hint: string;
  answer: string;
  slug: string;
}

export async function fetchTutorialSteps(): Promise<TutorialHint[]> {
  return (await get(`${process.env.REACT_APP_API_BASE}/users/self/tutorial/steps`, {
    isCredentialsIncluded: true,
    isFullUrl: true,
  })) as Promise<TutorialHint[]>;
}
export type TutorialStatus = 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED' | 'LOADING';
export type TutorialStepRequirement = 'simulate' | 'submit' | undefined | null;

export interface ConsultantTutorialStep {
  answer: string | null;
  hint: string | null;
  id: number;
  name: string;
  requirements: TutorialStepRequirement;
  slug: string;
  status: 'COMPLETED' | 'IN_PROGRESS' | 'NOT_STARTED';
  task: null | string;
  visited: boolean;
}

export interface ConsultantTutorialStepDetailsResponse {
  status: TutorialStatus;
  currentStep: number;
  active: boolean;
  steps: ConsultantTutorialStep[];
}

export async function fetchConsultantTutorialStepDetails(): Promise<ConsultantTutorialStepDetailsResponse> {
  // Return the normalized mock data

  return (await get('users/self/consultant/tutorial/summary', { isCredentialsIncluded: true })) as ConsultantTutorialStepDetailsResponse;
}

export async function patchConsultantTutorialStep(newStep: number) {
  const payload = { currentStep: newStep };
  return (await patch(`users/self/consultant/tutorial/summary`, {
    body: payload,
    isCredentialsIncluded: true,
    isResponseJson: false,
  })) as Promise<undefined>;
}

export async function patchConsultantTutorialActive(isActive: boolean) {
  const payload = { active: isActive };
  return (await patch(`users/self/consultant/tutorial/summary`, {
    body: payload,
    isCredentialsIncluded: true,
    isResponseJson: false,
  })) as Promise<undefined>;
}

interface Video {
  id: string;
  uid: string;
  source: string;
}

export interface VideoCourse {
  id: string;
  title: string;
  description: string;
  videos: Video[];
}

export interface VideoCourses {
  count: number;
  results: VideoCourse[];
}

export async function fetchLearnVideoCourses() {
  return (await get(ENDPOINTS_VIDEO_COURSES.videoCourses, {
    isCredentialsIncluded: true,
    isFullUrl: true,
  })) as Promise<VideoCourses>;
}

interface BoardChoice {
  value: string;
  label: string;
}

interface BoardsField {
  type: 'choice';
  required: boolean;
  readOnly: boolean;
  label: string;
  choices: BoardChoice[];
}

interface ActionsGet {
  board: BoardsField;
}

interface Actions {
  GET: ActionsGet;
}

interface PowerPoolRegionsResponse {
  actions: Actions;
}

export async function fetchPowerPoolRegions() {
  return (await requestOptions(`consultant/boards/power-pool`, {
    isCredentialsIncluded: true,
  })) as Promise<PowerPoolRegionsResponse>;
}

export interface UserPreferences {
  simulationWalkthrough?: boolean;
  language?: string; // one of SUPPORTED_BROWSER_LANGUAGE_CODES
  goldDialogShown?: boolean;
  // 'YYYY-QN' quarter the unlock modal was last shown in (at-most-once-per-quarter gate).
  streakFreezeUnlockShownQuarter?: string;
  // `freeze.usedDate` the protected modal was last shown for (dedupe per applied freeze).
  streakProtectedShownDate?: string;
}

export interface ClientSettings {
  version: number;
  preferences: UserPreferences;
}

export interface UserSelfResponse {
  id: string;
  email: string;
  telephone: string;
  firstName: string;
  lastName: string;
  fullName: string;
  gender: string;
  dateCreated: string;
  dateVerified: string;
  dateApproved: string;
  verified: boolean;
  approved: boolean;
  address: Address;
  education: Education;
  employment: unknown;
  recruitment: unknown;
  resume: Resume;
  image: UserImage;
  settings: UserSettings;
  onboarding: Onboarding;
  auxiliary: Auxiliary;
  geniusLevel: string;
}

export interface Address {
  street: string;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string;
}

export interface Education {
  university: string;
  major: string | null;
  degree: string;
  stem: string | null;
  graduationYear: number | null;
  gpa: number;
  maxGPA: number;
}

export interface Resume {
  dateCreated: string;
}

export interface UserImage {
  url: string;
}

export interface UserSettings {
  allowTracking: boolean;
  communication: Communication;
  privacy: Privacy;
  client?: ClientSettings;
}

export interface Communication {
  allowSMS: boolean;
}

export interface Privacy {
  name: VisibilitySetting;
  image: VisibilitySetting;
}

export interface VisibilitySetting {
  visibility: string;
  moderation: string;
}
export interface Onboarding {
  status: string;
}

export interface Auxiliary {
  campaign: Campaign;
}

export interface Campaign {
  campaign: string;
  source: string;
  medium: string;
  term: string | null;
  content: string | null;
}

// The backend shallow-merges the top level of `settings`, so we only send the `client` subtree.
// It responds with the full user object, from which we read back the persisted `client`.
export async function patchUserClientSettings(client: ClientSettings): Promise<ClientSettings | undefined> {
  const response = (await patch(`users/self`, {
    body: { settings: { client } },
    isCredentialsIncluded: true,
    isResponseJson: true,
    contentType: 'application/json',
  })) as UserSelfResponse;
  return response.settings?.client;
}

export type ISODateString = string;

export interface TutorialTextContent {
  id: string;
  type: 'TEXT';
  value: string;
}

export interface TutorialHeadingContent {
  id: string;
  type: 'HEADING';
  value: {
    level: string;
    content: string;
  };
}

export interface TutorialImageContent {
  id: string;
  type: 'IMAGE';
  value: {
    title: string;
    width: number;
    height: number;
    fileSize: number;
    url: string;
  };
}

export interface TutorialEquationContent {
  id: string;
  type: 'EQUATION';
  value: string;
}

export interface TutorialSimulationExampleContent {
  id: string;
  type: 'SIMULATION_EXAMPLE';
  value: {
    settings: {
      instrumentType: string;
      region: string;
      universe: string;
      delay: number;
      decay: number;
      neutralization: string;
      truncation: number;
      pasteurization: string;
      unitHandling: string;
      nanHandling: string;
      language: string;
      maxTrade: string;
      maxPosition: string;
    };
    type: string;
    regular: string;
  };
}

export interface TutorialTableContent {
  id: string;
  type: 'TABLE';
  value: {
    data: string[][];
    firstRowIsTableHeader: boolean;
    firstColIsHeader: boolean;
  };
}

export interface TutorialSvgContent {
  id: string;
  type: 'SVG';
  value: string; // url
}

export type TutorialItemContentType =
  | TutorialTextContent
  | TutorialHeadingContent
  | TutorialImageContent
  | TutorialEquationContent
  | TutorialSimulationExampleContent
  | TutorialTableContent
  | TutorialSvgContent;

export interface TutorialItemType {
  category: string;
  content: TutorialItemContentType[];
  id: string;
  lastModified: ISODateString;
  sequence: number;
  title: string;
}
